import { InputAdornment, Typography } from "@mui/material";
import { GridCellParams, GridRowModel } from "@mui/x-data-grid-pro";
import { DateTime } from "luxon";
import { FormatType } from "../../Alert/Filter/FilterType";
import {
  ChartPoint,
  ConfigurationPageType,
  ConfigurationTableFilterType,
  ConfigurationFormType,
  ConfigurationPageTables,
  ConfigurationDialogFormType,
  Item,
} from "../interfaces";
import { Roles } from "../../AuthProvider";
import { FormattedMessage, IntlShape } from "react-intl";
import ModelChip from "../../components/ModelChip";
import { REFERENCE_DASH } from "../../Theme";
import { dayWindow, isSameAlertDay } from "../../Cockpit/dateWindow";
import { BookingCurvePoint } from "../../Cockpit/types";
import {
  isFutureDate,
  isModelFromOtherProperty,
  modelFamilyFromRow,
  modelPropertyFromRow,
  modelWeekDayFromRow,
} from "./modelsCalendarHelpers";

// Dialog subtitle: the context the form no longer shows as fields, and the
// model in place as a chip (an unmapped day has none yet).
const modelCalendarSubtitle = (row: GridRowModel, intl: IntlShape) => (
  <>
    {[
      row.property?.hebergement_nom_court,
      DateTime.fromISO(row.date_at, { setZone: true })
        .setLocale(intl.locale)
        .toLocaleString(DateTime.DATE_HUGE),
      row.model ? intl.formatMessage({ id: "MODEL_CURVE_CURRENT" }) : null,
    ]
      .filter(Boolean)
      .join(" · ")}
    {row.model && (
      <ModelChip
        label={row.model.short_name ?? row.model.code}
        color={row.model.color}
      />
    )}
  </>
);

// A row is considered "modified" when it has an editor (fos_user). The editor
// is only set when a user edits the row (perform_update / serializer.validate),
// so its presence is the reliable signal that the row was changed by a user.
const highlightIfRowModified = (params: GridCellParams): string =>
  params.row.fos_user?.username ? "blueText" : "";

// "Date Eq." column: grey text by default, while keeping the blue highlight when
// the row was modified (blueText is declared after greyText in the theme, so it
// wins when both classes are present).
const dateEqClassName = (params: GridCellParams): string =>
  `greyText ${highlightIfRowModified(params)}`.trim();

const toFiniteNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

// `/api/models/{id}/curve/`, switched to the model's UH-type counterpart when
// a UH type is scoped (404 when the model has none for it).
const modelCurveUrl = (
  modelId: unknown,
  uhTypeId: number | null
): string | null => {
  if (typeof modelId !== "number") return null;
  const url = `/api/models/${modelId}/curve/`;
  return uhTypeId == null ? url : `${url}?uh_type_id=${uhTypeId}`;
};

interface ModelCurvePoint {
  lead_time: number;
  model: number | null;
}

// The curve endpoint gives the cumulated occupation rate (0..1) per lead time.
const modelCurvePoints = (data: unknown): ChartPoint[] =>
  Array.isArray(data)
    ? data.map((point: ModelCurvePoint) => ({
        leadTime: point.lead_time,
        value: point.model == null ? null : point.model * 100,
      }))
    : [];

// The realised booking curve of the row's property and day, as the cockpit
// MEC draws it (same endpoint; no `reservation_status` so the back applies
// the tenant's KPI statuses, the cockpit default), scoped to the UH type
// when one is picked.
const bookingCurveUrl = (
  row: GridRowModel | undefined,
  uhTypeId: number | null
): string | null => {
  if (!row?.property?.id || !row.date_at) return null;
  const params = new URLSearchParams({
    hebergement_id: String(row.property.id),
    date_occupation: String(row.date_at).slice(0, 10),
  });
  if (uhTypeId != null) params.set("uh_type_id", String(uhTypeId));
  return `/api/occupations/booking-curve/?${params.toString()}`;
};

// One reading of the booking-curve reply (a 0..1 share of capacity) as
// percentages; the realised and capacity series share the same cached call.
const bookingCurvePoints =
  (field: "pu_percent_cumul_n" | "capacite_percent") =>
  (data: unknown): ChartPoint[] => {
    const graph = (data as { Graph?: BookingCurvePoint[] | null } | null)
      ?.Graph;
    return (graph ?? []).map((point) => ({
      leadTime: point.lead_time_occupation,
      value: point[field] == null ? null : point[field] * 100,
    }));
  };

// Distinct UH types across the property's UH cats, in display order (a UH
// type has no property of its own, hence the detour through the cats).
const uhTypesFromCats = (cats: Item[]): { id: number; label: string }[] => {
  const byId = new Map<
    number,
    { id: number; label: string; order: number | null }
  >();
  cats.forEach((cat) => {
    const uhType = cat.uh_type_nandc;
    if (uhType?.id == null || byId.has(uhType.id)) return;
    byId.set(uhType.id, {
      id: uhType.id,
      label: uhType.short_name ?? `#${uhType.id}`,
      order: uhType.order ?? null,
    });
  });
  return [...byId.values()]
    .sort(
      (a, b) =>
        (a.order ?? 0) - (b.order ?? 0) || a.label.localeCompare(b.label)
    )
    .map(({ id, label }) => ({ id, label }));
};

/**
 * Prefills the model parameters from the standard-curve indicators carried by the
 * edited row, when a reference shape is picked.
 *
 * Both targets live in form space, not database space: `param_to_final` is entered
 * as a percentage (its "percent" inputType divides by 100 on save) while the source
 * columns are ratios, and `param_lt_init` is entered positive (its "negativeInteger"
 * inputType negates it on save) while `lt_debut_indiv_included` is negative or zero,
 * hence the absolute value.
 *
 * A missing source leaves its field untouched rather than clearing it, so picking a
 * reference shape on a model without a standard curve cannot wipe an existing setup.
 * `row` is undefined in mass edit, where there is no single row to read from.
 */
const prefillFromStandardCurve = ({
  row,
  setFieldValue,
}: {
  row?: GridRowModel;
  setFieldValue: (field: string, value: unknown) => void;
}): void => {
  if (!row) {
    return;
  }

  const indivOccRate = toFiniteNumber(row.pt_to_indiv_included);
  const nonIndivOccRate = toFiniteNumber(row.pt_to_non_indiv_included);
  if (indivOccRate !== null && nonIndivOccRate !== null) {
    setFieldValue(
      "param_to_final",
      Math.round((indivOccRate + nonIndivOccRate) * 100)
    );
  }

  const startLeadTimeIndiv = toFiniteNumber(row.lt_debut_indiv_included);
  if (startLeadTimeIndiv !== null) {
    setFieldValue("param_lt_init", Math.abs(startLeadTimeIndiv));
  }
};

const SETUP_MODELS_LIBRARY_PAGE: ConfigurationPageTables = {
  description: "CONF_DESC_SETUP_LIBRARY_MODELS",
  type: ConfigurationPageType.TABLE,
  label: "MODELS_LIBRARY_MENU",
  path: "models-librairy",
  model: {
    entityName: "name",
    label: "MODELS_LIBRARY_MENU",
    path: "/api/models",
    name: "ModelsLib",
    enableTableColumnMenu: true,
    attributes: [
      {
        className: "greyText",
        key: "code",
        order: 1,
        label: "CODE",
        visible: false,
        hideable: true,
      },
      {
        key: "short_name",
        order: 2,
        label: "SHORT_NAME",
        visible: true,
      },
      {
        key: "name",
        order: 3,
        label: "NAME",
        visible: false,
        hideable: true,
      },
      {
        key: "color",
        order: 4,
        label: "COLOR",
        visible: true,
        type: FormatType.COLOR,
        width: 10,
        hideable: true,
      },
      {
        key: "rank",
        order: 5,
        label: "RANK",
        visible: true,
        hideable: true,
      },
      {
        key: "nb_jours_included",
        order: 6,
        label: "NB_DAYS_INCLUDED",
        visible: true,
        hideable: true,
      },
      {
        key: "nb_jours_affectes_passe",
        order: 7,
        label: "NB_DAYS_AFFECTED",
        visible: true,
        hideable: true,
      },
      {
        key: "taux_spill_uh_included",
        order: 8,
        label: "SPILL_RATE_UH_INCLUDED",
        visible: true,
        type: FormatType.PERCENT,
        hideable: true,
      },
      {
        key: "lt_spill_moyen_uh_included",
        order: 9,
        label: "SPILL_LT_UH_INCLUDED",
        visible: true,
        type: FormatType.NUMBER,
        maximumFractionDigits: 0,
        hideable: true,
      },
      {
        key: "taux_contrainte_uh_included",
        order: 10,
        label: "CONSTRAINT_RATE_UH_INCLUDED",
        visible: true,
        type: FormatType.PERCENT,
        hideable: true,
      },
      {
        key: "lt_contrainte_moyen_uh_included",
        order: 11,
        label: "CONSTRAINT_LT_UH_INCLUDED",
        visible: true,
        type: FormatType.NUMBER,
        maximumFractionDigits: 0,
        hideable: true,
      },
      {
        key: "pt_to_decontrainte_included",
        order: 12,
        label: "UNCONSTRAINT_ADDITIONAL_OR_INCLUDED",
        visible: true,
        type: FormatType.PERCENT,
        hideable: true,
      },
      {
        key: "lt_debut_indiv_included",
        order: 13,
        label: "LT_START_INDIV_INCLUDED",
        visible: true,
        hideable: true,
      },
      {
        key: "pt_to_indiv_included",
        order: 14,
        label: "INDIV_OCC_RATE_INCLUDED",
        visible: true,
        type: FormatType.PERCENT,
        hideable: true,
      },
      {
        key: "pt_to_non_indiv_included",
        order: 15,
        label: "NON_INDIV_OCC_RATE_INCLUDED",
        visible: true,
        type: FormatType.PERCENT,
        hideable: true,
      },
      {
        key: "ca_final",
        className: "progressCA",
        order: 16,
        label: "FINAL_TURNOVER",
        tooltipLabel: "FINAL_TURNOVER_TOOLTIP",
        sortingOrder: ["desc", "asc", null],
        type: FormatType.PROGRESS_CURRENCY,
        visible: true,
        hideable: true,
      },
      {
        key: "to_final",
        className: "progressOcc",
        order: 17,
        label: "FINAL_TO",
        tooltipLabel: "FINAL_TO_TOOLTIP",
        isPercentValue: true,
        sortingOrder: ["desc", "asc", null],
        type: FormatType.PROGRESS,
        visible: true,
        hideable: true,
      },
      {
        key: "pm_raf_init",
        className: "progressPM",
        order: 18,
        label: "INIT_PM_RAF",
        tooltipLabel: "INIT_PM_RAF_TOOLTIP",
        sortingOrder: ["desc", "asc", null],
        type: FormatType.PROGRESS_CURRENCY,
        visible: true,
        hideable: true,
      },
      {
        key: "pm_raf_final",
        className: "progressPM2",
        order: 19,
        label: "FINAL_PM_RAF",
        tooltipLabel: "FINAL_PM_RAF_TOOLTIP",
        sortingOrder: ["desc", "asc", null],
        type: FormatType.PROGRESS_CURRENCY,
        visible: true,
        hideable: true,
      },
      {
        key: "model_type.short_name",
        order: 20,
        label: "MODEL_TYPE",
        visible: true,
        hideable: true,
      },
      {
        key: "model_sponsor.short_name",
        order: 21,
        label: "MODEL_SPONSOR",
        visible: true,
        hideable: true,
      },
      {
        key: "influence_pm",
        order: 22,
        label: "INFLUENCE_PM",
        type: FormatType.PERCENT,
        visible: true,
        hideable: true,
      },
      {
        key: "param_to_final",
        order: 23,
        label: "PARAM_TO_FINAL",
        type: FormatType.PERCENT,
        visible: true,
        hideable: true,
      },
      {
        key: "param_to_init",
        order: 24,
        label: "PARAM_TO_INIT",
        type: FormatType.PERCENT,
        visible: true,
        hideable: true,
      },
      {
        key: "param_lt_init",
        order: 25,
        label: "PARAM_LT_INIT",
        visible: true,
        hideable: true,
      },
      {
        key: "param_pm",
        order: 26,
        label: "PARAM_PM",
        type: FormatType.CURRENCY,
        visible: true,
        hideable: true,
      },
      {
        key: "created_at",
        order: 27,
        label: "CREATED_AT",
        visible: true,
        type: FormatType.DATE,
        hideable: true,
      },
      {
        key: "updated_at",
        order: 28,
        label: "UPDATED_AT",
        visible: true,
        type: FormatType.DATE,
        hideable: true,
      },
      {
        key: "editor.username",
        order: 29,
        label: "EDITED_BY",
        visible: true,
        hideable: true,
      },
    ],
    filters: [
      {
        type: ConfigurationTableFilterType.WithPastDate,
        api: "/api/models/with-past-date-count/",
      },
      {
        type: ConfigurationTableFilterType.SelectMultiple,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "hebergement_nom_court",
        labelKey: "hebergement_nom_court",
        label: "PROPERTY",
        lookup: "property",
        mustTranslateOptions: false,
        controlledInputSize: true,
      },
      {
        type: ConfigurationTableFilterType.SelectMultiple,
        mustTranslateOptions: false,
        api: "/api/uh-types-nandc/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "uh_type_nandc",
        labelKey: "short_name",
        label: "UHTYPES_NC",
        lookup: "uh_type_nandc",
        controlledInputSize: true,
      },
      {
        type: ConfigurationTableFilterType.SelectMultiple,
        mustTranslateOptions: false,
        api: "/api/models-family/",
        apiParams: {
          page_size: 100,
          ordering: "name",
        },
        key: "family",
        labelKey: "name",
        label: "MODEL_FAMILY",
        lookup: "family",
        renderOption: {
          type: "color",
          key: "color",
        },
        controlledInputSize: true,
      },
      {
        type: ConfigurationTableFilterType.SelectMultiple,
        mustTranslateOptions: false,
        api: "/api/models/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "model_sponsor",
        labelKey: "short_name",
        label: "MODEL_SPONSOR",
        lookup: "model_sponsor",
        renderOption: {
          type: "color",
          key: "color",
        },
        controlledInputSize: true,
      },
      {
        type: ConfigurationTableFilterType.DayOfWeek,
        lookup: "week_day",
        label: "DAY_OF_WEEK",
      },
      {
        type: ConfigurationTableFilterType.DatePicker,
        lookup: "created_at",
        label: "CREATED_AT",
      },
      {
        type: ConfigurationTableFilterType.Search,
      },
      {
        type: ConfigurationTableFilterType.ResetButton,
      },
    ],
    export: { path: "/api/models/export" },
    import: { path: "/api/models/presign-url" },
  },
  options: {
    formFields: [
      {
        key: "code",
        label: "CODE",
        type: ConfigurationFormType.Input,
      },
      {
        key: "short_name",
        label: "SHORT_NAME",
        type: ConfigurationFormType.Input,
      },
      {
        key: "name",
        label: "NAME",
        type: ConfigurationFormType.Input,
      },
      {
        key: "sectionModel",
        label: "MODEL_FORM",
        type: ConfigurationFormType.Separator,
      },
      {
        key: "model_sponsor",
        label: "MODEL_SPONSOR",
        type: ConfigurationFormType.Select,
        api: "/api/models/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        column: "model_sponsor.short_name",
        labelKey: "short_name",
        watchField: "model_type",
        watchedBy: "model_type",
        watchFilledField: "model_type",
      },
      {
        key: "model_type",
        label: "MODEL_TYPE",
        type: ConfigurationFormType.Select,
        api: "/api/models-type/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        column: "model_type.short_name",
        labelKey: "short_name",
        watchField: "model_sponsor",
        watchedBy: "model_sponsor",
        watchFilledField: "model_sponsor",
        onValueChange: prefillFromStandardCurve,
      },
      {
        key: "param_lt_init",
        label: "PARAM_LT_INIT",
        inputType: "negativeInteger",
        inputProps: {
          inputMode: "numeric",
          startAdornment: (
            <InputAdornment position="start">
              <FormattedMessage id={"X_DAYS_PREFIX"} />
            </InputAdornment>
          ),
        },
        tooltip: "X_DAYS_PREFIX_EXPLANATION",
        type: ConfigurationFormType.Input,
      },
      {
        key: "param_to_final",
        label: "PARAM_TO_FINAL",
        inputType: "percent",
        inputProps: {
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        },
        type: ConfigurationFormType.Input,
      },
      {
        key: "param_to_init",
        label: "PARAM_TO_INIT",
        inputType: "percent",
        inputProps: {
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        },
        type: ConfigurationFormType.Input,
      },
      {
        key: "sectionParam",
        label: "MODEL_PRICE",
        type: ConfigurationFormType.Separator,
      },
      {
        key: "influence_pm",
        label: "INFLUENCE_PM",
        inputType: "percent",
        inputProps: {
          inputMode: "numeric",
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        },
        type: ConfigurationFormType.Input,
      },
      {
        key: "param_pm",
        label: "PARAM_PM",
        inputType: "currency",
        type: ConfigurationFormType.Input,
      },
    ],
    creatable: false,
    copyable: false,
    editable: true,
    editOptions: [
      {
        key: "code",
        flex: "50%",
        required: true,
      },
      {
        key: "short_name",
        flex: "50%",
        required: true,
      },
      {
        key: "name",
        flex: "100%",
        required: true,
      },
      {
        key: "sectionModel",
        flex: "100%",
      },
      {
        key: "model_sponsor",
        flex: "100%",
      },
      {
        key: "model_type",
        flex: "50%",
      },
      {
        key: "param_lt_init",
        flex: "50%",
      },
      {
        key: "param_to_final",
        flex: "50%",
      },
      {
        key: "param_to_init",
        flex: "50%",
      },
      {
        key: "sectionParam",
        flex: "100%",
      },
      {
        key: "influence_pm",
        flex: "50%",
      },
      {
        key: "param_pm",
        flex: "50%",
      },
    ],
    deletable: true,
  },
};

export const SETUP_MODELS_CALENDAR_PAGE: ConfigurationPageTables = {
  description: "CONF_DESC_SETUP_CALENDAR_MODELS",
  type: ConfigurationPageType.TABLE,
  label: "CALENDAR_MODELS",
  path: "models-calendar",
  model: {
    entityName: "name",
    label: "MODEL",
    subtitle: {
      dialogFormTypes: [ConfigurationDialogFormType.EDIT],
      label: modelCalendarSubtitle,
    },
    path: "/api/models-calendar",
    name: "Calendar",
    dynamicPath: true,
    attributes: [
      {
        key: "property.hebergement_nom_court",
        order: 1,
        label: "SHORT_NAME",
      },
      {
        key: "date_at",
        type: FormatType.DATE,
        order: 2,
        label: "DATE",
        specificFormat: "ccc d LLLL yyyy",
      },
      {
        key: "model.color,model.short_name",
        type: FormatType.MODEL_COLOR,
        order: 2,
        sortField: "model.short_name",
        label: "MODEL",
      },
      {
        key: "events",
        type: FormatType.EVENT,
        order: 2,
        label: "EVENT",
      },
      {
        key: "related_date",
        type: FormatType.DATE,
        order: 2,
        label: "RELATED_DATE",
        specificFormat: "ccc d LLLL yyyy",
        className: dateEqClassName,
      },
      {
        key: "related_model.color,related_model.short_name",
        type: FormatType.MODEL_COLOR,
        order: 2,
        sortField: "related_model.short_name",
        label: "RELATED_MODEL",
        className: highlightIfRowModified,
      },
      {
        key: "related_property_events",
        type: FormatType.EVENT,
        order: 2,
        label: "RELATED_EVENT",
      },
      {
        key: "related_property.hebergement_nom_court",
        order: 2,
        label: "RELATED_PROPERTY",
      },
      {
        key: "fos_user.username",
        order: 2,
        label: "EDITED_BY",
      },
      {
        key: "comment",
        order: 2,
        label: "COMMENT",
        tooltip: true,
      },
    ],
    filters: [
      {
        type: ConfigurationTableFilterType.Unmapped,
        api: "/api/models-calendar/?is_unmapped=1",
      },
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "property",
        labelKey: "hebergement_nom_court",
        label: "PROPERTY",
        lookup: "property",
      },
      {
        type: ConfigurationTableFilterType.SelectMultiple,
        mustTranslateOptions: false,
        api: "/api/models/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "model",
        labelKey: "short_name",
        label: "MODEL",
        lookup: "model",
        renderOption: {
          type: "color",
          key: "color",
        },
        controlledInputSize: true,
      },
      {
        type: ConfigurationTableFilterType.SelectMultiple,
        mustTranslateOptions: false,
        api: "/api/models/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "related_model",
        labelKey: "short_name",
        label: "RELATED_MODEL",
        lookup: "related_model",
        renderOption: {
          type: "color",
          key: "color",
        },
        controlledInputSize: true,
      },
      {
        type: ConfigurationTableFilterType.DayOfWeek,
        lookup: "week_day",
        label: "DAY_OF_WEEK",
      },
      {
        type: ConfigurationTableFilterType.DatePicker,
        lookup: "date_at__gte",
        label: "START_DATE",
        defaultToday: true,
      },
      {
        type: ConfigurationTableFilterType.DatePicker,
        lookup: "date_at__lte",
        label: "END_DATE",
      },
      {
        type: ConfigurationTableFilterType.Search,
      },
      {
        type: ConfigurationTableFilterType.ResetButton,
      },
    ],
    export: { path: "/api/models-calendar/export" },
    import: { path: "/api/models-calendar/presign-url" },
    actions: [
      {
        type: "trigger",
        api: "/api/reco-pricing-initial/compute/",
        label: "COMPUTE_RECO_INITIAL_PRICING",
        requiredRole: Roles.SuperAdmin,
      },
    ],
  },
  options: {
    formFields: [
      {
        type: ConfigurationFormType.RadioGroup,
        key: "model_affectation",
        label: "MODEL_AFFECTATION",
        virtual: true,
        defaultValue: "model",
        watchedBy: "related_property",
        // A saved row does not tell which mode it was saved in (the related
        // property is optional): the Configuration opens on "reuse", the
        // cockpit imposes its own default through DynamicForm `initialValues`.
        initialValueFromRow: () => "property",
        options: [
          {
            value: "property",
            label: "MODEL_AFFECTATION_RADIO_PROPERTY",
            description: "MODEL_AFFECTATION_RADIO_PROPERTY_DESC",
          },
          {
            value: "model",
            label: "MODEL_AFFECTATION_RADIO_MODEL",
            description: "MODEL_AFFECTATION_RADIO_MODEL_DESC",
          },
        ],
      },
      {
        type: ConfigurationFormType.Separator,
        key: "assignment_separator",
        label: "",
      },
      {
        type: ConfigurationFormType.DatePicker,
        key: "date_at",
        label: "DATE",
      },
      {
        key: "related_date",
        label: "RELATED_DATE_FULL",
        tooltip: "RELATED_DATE_TOOLTIP",
        type: ConfigurationFormType.DatePicker,
        // The picker's intrinsic width (adornments included) slightly exceeds
        // half the dialog: let it shrink to its share so the property stays
        // beside it.
        wrapperSx: { minWidth: 0 },
        relatedInputAction: {
          action: "hide",
          relatedInput: "model_affectation",
          equals: "model",
        },
      },
      {
        // Optional: left empty, the model is taken from the row's own property.
        type: ConfigurationFormType.Select,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "related_property",
        label: "RELATED_PROPERTY_OPTIONAL",
        labelKey: "hebergement_nom_court",
        wrapperSx: { minWidth: 0 },
        relatedInputAction: {
          action: "hide",
          relatedInput: "model_affectation",
          equals: "model",
        },
      },
      {
        type: ConfigurationFormType.RemoteLookup,
        key: "related_model_lookup",
        label: "RELATED_MODEL_PREVIEW",
        virtual: true,
        api: "/api/models-calendar/",
        lookupParams: (values, row) => {
          const window = dayWindow(values.related_date);
          const propertyId = values.related_property?.id ?? row?.property?.id;
          return propertyId && window
            ? {
                property: propertyId,
                date_at__gte: window.gte,
                date_at__lt: window.lt,
                page_size: 10,
              }
            : null;
        },
        pick: (items, values) => {
          const window = dayWindow(values.related_date);
          return (
            (window &&
              items.find(
                (item) => item.model && isSameAlertDay(item.date_at, window.day)
              )) ??
            null
          );
        },
        writeTo: {
          field: "model",
          // `property` is the model's own hotel (the source row may itself
          // borrow from elsewhere): the inclusion rule reads it.
          value: (item) =>
            item
              ? {
                  id: item.model.id,
                  label: item.model.short_name,
                  property: item.model.property,
                }
              : null,
        },
        render: (item, intl) => (
          <>
            <ModelChip label={item.model.short_name} color={item.model.color} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {item.property?.hebergement_nom_court} ·{" "}
              {DateTime.fromISO(item.date_at, { setZone: true })
                .setLocale(intl.locale)
                .toLocaleString(DateTime.DATE_MED)}
            </Typography>
          </>
        ),
        notFoundLabel: "RELATED_MODEL_NOT_FOUND",
        relatedInputAction: {
          action: "hide",
          relatedInput: "model_affectation",
          equals: "model",
        },
      },
      {
        type: ConfigurationFormType.Select,
        key: "model_family",
        label: "MODEL",
        virtual: true,
        api: "/api/models-family/",
        apiParams: { page_size: 100, ordering: "rank" },
        labelKey: "name",
        keepApiData: ["color"],
        renderOption: { type: "color", key: "color" },
        initialValueFromRow: modelFamilyFromRow,
        relatedInputAction: {
          action: "hide",
          relatedInput: "model_affectation",
          equals: "property",
        },
      },
      {
        type: ConfigurationFormType.Select,
        key: "model_property",
        label: "MODEL_PROPERTY",
        virtual: true,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        labelKey: "hebergement_nom_court",
        tooltip: "MODEL_PROPERTY_TOOLTIP",
        initialValueFromRow: modelPropertyFromRow,
        relatedInputAction: {
          action: "hide",
          relatedInput: "model_affectation",
          equals: "property",
        },
      },
      {
        type: ConfigurationFormType.DayOfWeek,
        key: "model_week_day",
        label: "MODEL_WEEK_DAY",
        tooltip: "MODEL_WEEK_DAY_TOOLTIP",
        virtual: true,
        initialValueFromRow: modelWeekDayFromRow,
        relatedInputAction: {
          action: "hide",
          relatedInput: "model_affectation",
          equals: "property",
        },
      },
      {
        type: ConfigurationFormType.RemoteLookup,
        key: "model_lookup",
        label: "SELECTED_MODEL",
        virtual: true,
        api: "/api/models/",
        lookupParams: (values) =>
          values.model_family?.id &&
          values.model_property?.id &&
          values.model_week_day
            ? {
                family: values.model_family.id,
                property: values.model_property.id,
                week_day: values.model_week_day,
                page_size: 100,
              }
            : null,
        // The API also returns the UH-type derivatives of the same family,
        // hotel and day; the calendar assigns the hotel-level model only.
        pick: (items) =>
          items.find((item) => item.uh_type_nandc == null) ?? null,
        writeTo: {
          field: "model",
          value: (item) =>
            item
              ? { id: item.id, label: item.short_name, property: item.property }
              : null,
        },
        render: (item) => (
          <ModelChip label={item.short_name} color={item.color} />
        ),
        notFoundLabel: "MODEL_NOT_FOUND_FOR_CONTEXT",
        relatedInputAction: {
          action: "hide",
          relatedInput: "model_affectation",
          equals: "property",
        },
      },
      {
        // The backend field, filled by the lookups above and below.
        key: "model",
        label: "MODEL",
        type: ConfigurationFormType.Select,
        api: "/api/models/",
        labelKey: "short_name",
      },
      {
        type: ConfigurationFormType.Check,
        key: "is_included",
        label: "INCLUDED_IN_MODEL_CURVE",
        tooltip: "INCLUDED_TOOLTIP",
        // Compact: no top offset, and the box's vertical touch padding goes.
        wrapperSx: { mt: 0, "& .MuiCheckbox-root": { py: 0 } },
        hiddenIf: (row) => isFutureDate(row),
        disabledIf: isModelFromOtherProperty,
        disabledHint: "INCLUDED_OTHER_PROPERTY_HINT",
      },
      {
        type: ConfigurationFormType.TextArea,
        key: "comment",
        label: "COMMENT",
        // Its label sits above the field and needs the room.
        wrapperSx: { mt: 1 },
      },
      {
        type: ConfigurationFormType.Text,
        key: "manual_change_warning",
        label: "MODEL_CHANGE_MANUAL_WARNING",
        status: "warning",
        icon: true,
        sx: { textAlign: "left" },
      },
      {
        type: ConfigurationFormType.Chart,
        key: "graph_model_preview",
        label: "MODEL_PREVIEW",
        chart: "leadTimeCurve",
        date: (_values, row) => row?.date_at ?? "",
        scope: {
          label: "UH_TYPE",
          allLabel: "PROPERTY",
          api: "/api/uh-cats/",
          lookupParams: (_values, row) =>
            row?.property?.id
              ? {
                  property: row.property.id,
                  is_active: true,
                  page_size: 200,
                  ordering: "code",
                }
              : null,
          options: uhTypesFromCats,
        },
        // Both modes write `model`: one chart follows the selection whatever
        // the mode, dotted (not applied yet) over the solid model assigned
        // today. Drawn second so it stays visible where the two coincide.
        series: [
          {
            key: "capacity",
            label: "CAPACITY",
            url: (_values, row, scope) => bookingCurveUrl(row, scope),
            points: bookingCurvePoints("capacite_percent"),
            color: "negative",
          },
          {
            key: "current",
            label: "MODEL_CURVE_CURRENT",
            url: (_values, row, scope) => modelCurveUrl(row?.model?.id, scope),
            points: modelCurvePoints,
            emptyLabel: "MODEL_CURVE_NOT_COMPUTED",
            notFoundLabel: "MODEL_CURVE_NO_UH_TYPE_VARIANT",
          },
          {
            key: "selected",
            label: "MODEL_CURVE_SELECTED",
            url: (values, _row, scope) =>
              modelCurveUrl(values.model?.id, scope),
            points: modelCurvePoints,
            dashArray: REFERENCE_DASH,
            lineCap: "round",
            emptyLabel: "MODEL_CURVE_NOT_COMPUTED",
            notFoundLabel: "MODEL_CURVE_NO_UH_TYPE_VARIANT",
          },
          {
            key: "realise",
            label: "COCKPIT_MEC_REALISE",
            url: (_values, row, scope) => bookingCurveUrl(row, scope),
            points: bookingCurvePoints("pu_percent_cumul_n"),
            color: "positive",
          },
        ],
      },
    ],
    additionalForms: [
      {
        name: "property-sponsor",
        label: "PROPERTY_TO_SPONSOR_FORM",
        labelButton: "SPONSOR_HOTEL",
        path: "/api/hebergements/sponsor-hebergement",
        dynamicPath: true,
        formFields: [
          {
            type: ConfigurationFormType.Text,
            key: "generalInfo",
            label: "SPONSOR_EXPLANATION",
            status: "nancie",
            icon: true,
            sx: {
              textAlign: "left",
              fontStyle: "normal",
            },
          },
          {
            type: ConfigurationFormType.Select,
            api: "/api/properties/",
            apiParams: {
              page_size: 100,
              ordering: "hebergement_nom_court",
            },
            key: "hebergement_id",
            label: "PROPERTY_TO_SPONSOR",
            labelKey: "hebergement_nom_court",
            watchedBy: "hebergement_id_sponso",
          },
          {
            type: ConfigurationFormType.Select,
            api: "/api/hebergements/sponsoring-hebergements/",
            apiParams: {
              page_size: 100,
              ordering: "hebergement_nom_court",
            },
            key: "hebergement_id_sponso",
            label: "PROPERTY_SPONSOR",
            labelKey: "hebergement_nom_court",
            tooltip: "PROPERTY_SPONSOR_TOOLTIP",
            watchField: [
              {
                paramsKey: "sponsored_hebergement_id",
                paramsValue: "id",
                watchFieldPath: "hebergement_id",
              },
            ],
          },
          {
            key: "date_range",
            startDateField: "start_date",
            startDateTooltip: "DATE_START_MODEL_SPONSOR_TOOLTIP",
            endDateField: "end_date",
            startDateLabel: "DATE_START",
            endDateLabel: "DATE_END",
            type: ConfigurationFormType.DateRangePicker,
            defaultValue: null,
          },
          {
            key: "separatorModel",
            label: "MODEL_DEFORMATION",
            type: ConfigurationFormType.Separator,
          },
          {
            key: "influence_pm",
            label: "INFLUENCE_PM",
            tooltip: "MODEL_DEFORMATION_EXPLANATION",
            inputType: "percent",
            inputProps: {
              inputMode: "numeric",
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            },
            type: ConfigurationFormType.Input,
          },
          {
            type: ConfigurationFormType.Text,
            key: "separatorINFO",
            label: "SPONSOR_INFO_EXPLANATION",
            separator: true,
            status: "warning",
            icon: true,
            sx: {
              textAlign: "left",
              fontStyle: "normal",
            },
          },
        ],
        options: [
          {
            key: "generalInfo",
            flex: "100%",
            readonly: true,
          },
          {
            key: "hebergement_id",
            flex: "50%",
            required: true,
          },
          {
            key: "hebergement_id_sponso",
            flex: "50%",
            required: true,
          },
          {
            key: "date_range",
            flex: "100%",
          },
          { key: "separatorModel", flex: "100%" },
          {
            key: "influence_pm",
            flex: "50%",
          },
          { key: "separatorINFO", flex: "100%" },
        ],
      },
    ],
    creatable: false,
    copyable: false,
    editable: true,
    editOptions: [
      {
        key: "model_affectation",
        flex: "100%",
      },
      {
        key: "date_at",
        flex: "100%",
        visible: false,
      },
      {
        key: "assignment_separator",
        flex: "100%",
      },
      {
        key: "related_date",
        flex: "50%",
        required: true,
      },
      {
        key: "related_property",
        flex: "50%",
      },
      {
        key: "related_model_lookup",
        flex: "100%",
      },
      {
        key: "model_family",
        flex: "100%",
        required: true,
      },
      {
        key: "model",
        flex: "100%",
        visible: false,
        required: true,
      },
      {
        key: "model_property",
        flex: "50%",
      },
      {
        key: "model_week_day",
        flex: "50%",
      },
      {
        key: "model_lookup",
        flex: "100%",
      },
      {
        key: "graph_model_preview",
        flex: "100%",
      },
      {
        key: "is_included",
        flex: "50%",
        required: true,
      },
      {
        key: "comment",
        flex: "100%",
        required: true,
      },
      {
        key: "manual_change_warning",
        flex: "100%",
      },
    ],
    deletable: false,
  },
};

export const SETUP_MODELS_PAGE = {
  type: ConfigurationPageType.TABS,
  count: 0,
  label: "MODELS",
  pageLabel: "MODELS",
  menuLabel: "MODELS",
  name: "Models",
  path: "models",
  hideInMenu: true,
  tabs: [SETUP_MODELS_CALENDAR_PAGE, SETUP_MODELS_LIBRARY_PAGE],
};
